import * as echarts from "echarts";

// Store ECharts instances for cleanup and resizing
const chartInstances = new WeakMap<HTMLElement, echarts.ECharts>();

export const getECharts = (): typeof echarts => {
    return echarts;
};



// Default color palette
export const getColors = (theme: 'light' | 'dark' = 'light') => {
    if (theme === 'dark') {
        return [
            "#212529", // primary brand
            "#343a40",
            "#495057",
            "#868e96",
            "#adb5bd",
            "#ced4da",
            "#dee2e6",
            "#e9ecef",
            "#aaaaaa",
            "#888888",
        ];
    }
    return [
        "#212529", // primary brand
        "#343a40",
        "#495057",
        "#868e96",
        "#adb5bd",
        "#ced4da",
        "#dee2e6",
        "#e9ecef",
        "#000000",
        "#404040",
    ];
};

/**
 * Blend a hex color toward white by a given ratio (0 = original, 1 = white).
 */
const blendTowardWhite = (hex: string, ratio: number): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const nr = Math.round(r + (255 - r) * ratio);
    const ng = Math.round(g + (255 - g) * ratio);
    const nb = Math.round(b + (255 - b) * ratio);
    return `#${nr.toString(16).padStart(2, '0')}${ng.toString(16).padStart(2, '0')}${nb.toString(16).padStart(2, '0')}`;
};

/**
 * Resolve colors based on config and theme.
 * In dark mode the palette is reversed and blended toward white for visibility.
 */
export const resolveColors = (config: any, theme: 'light' | 'dark' = 'light'): string[] => {
    const defaultColors = getColors(theme);
    const base: string[] = (config.colors && Array.isArray(config.colors) && config.colors.length > 0)
        ? config.colors
        : defaultColors;

    if (theme === 'dark') {
        return [...base].reverse().map(c => {
            if (/^#[0-9a-fA-F]{6}$/.test(c)) return blendTowardWhite(c, 0.3);
            return c;
        });
    }

    return base;
};

/**
 * Get or create ECharts instance for a container
 */
export const getChartInstance = (container: HTMLElement, theme: 'light' | 'dark' = 'light'): echarts.ECharts => {
    // Dispose existing instance if any
    const existingInstance = chartInstances.get(container);
    if (existingInstance) {
        existingInstance.dispose();
    }

    // Lazy load echarts
    const echarts = getECharts();

    // Create new instance with theme
    const instance = echarts.init(container, theme);
    chartInstances.set(container, instance);
    return instance;
};

/**
 * Clear container and dispose chart instance
 */
export const clearContainer = (container: HTMLElement | null): void => {
    if (!container) return;

    const instance = chartInstances.get(container);
    if (instance) {
        instance.dispose();
        chartInstances.delete(container);
    }

    // Clean up ResizeObserver if it exists
    if (container._resizeObserver) {
        container._resizeObserver.disconnect();
        delete container._resizeObserver;
    }

    // Clear container HTML
    container.innerHTML = "";
};

/**
 * Setup responsive resizing for a chart instance
 */
export const setupResize = (
    container: HTMLElement,
    instance: echarts.ECharts
): (() => void) => {
    const resizeHandler = (): void => {
        if (instance && !instance.isDisposed()) {
            instance.resize();
        }
    };

    // Resize on window resize
    window.addEventListener("resize", resizeHandler);

    // Also use ResizeObserver for container size changes
    if (window.ResizeObserver && container) {
        const resizeObserver = new ResizeObserver(() => {
            resizeHandler();
        });
        resizeObserver.observe(container);

        // Store observer for cleanup
        container._resizeObserver = resizeObserver;
    }

    return resizeHandler;
};

// Extend HTMLElement interface for ResizeObserver storage
declare global {
    interface HTMLElement {
        _resizeObserver?: ResizeObserver;
    }
}
